import React, { FC, Fragment, useCallback, useContext, useMemo, useRef } from 'react'
import HomePageTransport from '../../transports/home-page-transport'
import NewsLetterSection from '../common/news-letter-section'
import CommonHeroSection from '../common/hero-section'
import { HOME_BACKGROUND_IMAGES } from '../common/hero-section/constants'
import TrustBar from '../common/trust-bar-section'
import MegaBar from '../common/megabar'
import PropertyTypesSection from './property-types'
import { Container, InnerContainer } from './styles'
import OfferCardsSection from './offer-cards-section'
import UserDetailsModel from '../../models/user/user-details-model'
import { ScreenContext } from 'react-components'
import { useScrollToSearch } from '../../hooks/scroll-to-search'
import HeaderSection from '../common/header-section'
import RegionCountSection from './region-count-section'

import HolidayIdeaSection from './holiday-ideas'
import PopularSearchesSection from './popular-searches'
import { getPopularSearchesByLocale } from './popular-searches/popular-searches-data'
import { useTranslation, useLocale } from '../../hooks/locale'
import { BreadcrumbList } from '../../models/common/breadcrumb-model'
import { Offer } from '../../models/common/offers-model'
import { SuitablesModel } from '../../models/common/suitables-model'
import { useDateFormatter } from '../../hooks/date'
import ReviewSection from './review-section'
import OtherPopularRegionSection from './other-popular-region-section'
import FooterSection from '../common/footer'
import FaqSection from './faq-section'
import BannerSection from './banner-section/index'
import SeoSection from './seo-section'
import ContinueSearchWidget from './continue-search-widget'
import TopOffersSection from './top-offers-section'
import TopRecommendationsSection from './top-recommendations-section'
import RecentSearchesWidget from '../common/recent-searches-widget'
import { AB_KEY, AB_VARIANT_VERSION, LOCALES } from '../../common/constants'
import { useABVariant } from '../../utils/hooks'

type Props = {
    isLoggedIn: boolean
    userDetails: UserDetailsModel | null
    url: string
    homeDetails: HomePageTransport | null
}

const HomePage: FC<Props> = ({ userDetails, isLoggedIn, url, homeDetails }) => {
    const { t } = useTranslation()
    const { format } = useDateFormatter()
    const { locale } = useLocale()
    const breadcrumbModel = useMemo(() => new BreadcrumbList(t), [t])
    const offerModel = useMemo(() => new Offer(t, format), [t, format])
    const suitablesModel = useMemo(() => new SuitablesModel(t), [t])
    const { isMobile } = useContext(ScreenContext)

    const { isProvidedVariantActive: isPopularSearchesActive } = useABVariant(
        AB_KEY.POPULAR_SEARCHES,
        AB_VARIANT_VERSION.ONE,
    )

    const searchbarRef = useRef<HTMLDivElement>(null)
    const showHeaderSearch = useScrollToSearch(searchbarRef as React.RefObject<HTMLDivElement>, isMobile)

    const scrollToSearchbar = useCallback(() => {
        if (searchbarRef.current) {
            searchbarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [])

    if (!homeDetails) {
        return null
    }

    const {
        staticData,
        trustBar,
        propertyTypeSection,
        pageSubTypeResponse,
        holidayIdeas,
        regionMap,
        topRatedObjects: topRatedObjectsSection,
        otherPopularRegions: otherPopularRegionsSection,
        topOffers,
        recommendedObjects,
    } = homeDetails

    const {
        navigationSection,
        megabar,
        heroSection,
        offerCardSection,
        newsLetterSection,
        footerSection,
        banners,
        faqSection,
        seoSection,
    } = staticData

    const headerSection = { navigationSectionResponse: navigationSection }

    return (
        <Fragment>
            {navigationSection ? (
                <HeaderSection
                    firstName={userDetails?.firstName}
                    isLoggedIn={isLoggedIn}
                    url={url}
                    headerSection={headerSection}
                    showVacationIdeasNavButton
                    megabar={megabar}
                    onSearchIconClick={showHeaderSearch ? scrollToSearchbar : undefined}
                />
            ) : null}

            {!!megabar && <MegaBar megabar={megabar} />}

            <Container>
                {!!heroSection && (
                    <CommonHeroSection
                        title={heroSection.title}
                        subtitle={heroSection.subtitle}
                        backgroundImages={HOME_BACKGROUND_IMAGES}
                        ref={searchbarRef}
                        logo={headerSection?.navigationSectionResponse?.navigation?.logo}
                        textColor="white"
                        pageSubTypeResponse={pageSubTypeResponse}
                        isBorderGradientEnabled
                        showSliderSection
                        isIndexableTabs
                    />
                )}
                {!!trustBar && <TrustBar trustBar={trustBar} />}
                <InnerContainer>
                    <RecentSearchesWidget />
                    {locale === LOCALES.DE_DE && isPopularSearchesActive && (
                        <PopularSearchesSection items={getPopularSearchesByLocale(locale)} />
                    )}

                    <ContinueSearchWidget
                        url={url}
                        breadcrumbModel={breadcrumbModel}
                        offerModel={offerModel}
                        suitablesModel={suitablesModel}
                    />
                    {!!offerCardSection && <OfferCardsSection offerCardSection={offerCardSection} />}
                    {!!propertyTypeSection?.items?.length && (
                        <PropertyTypesSection
                            section={propertyTypeSection}
                            totalOnlineListingsCount={trustBar?.totalOnlineListingsCount}
                        />
                    )}
                    {!!holidayIdeas?.items && holidayIdeas.items.length > 0 && (
                        <HolidayIdeaSection items={holidayIdeas?.items} />
                    )}
                    {!!topOffers && topOffers?.objects?.length > 0 && (
                        <TopOffersSection
                            listingSection={topOffers}
                            url={url}
                            defaultHeading={t('pages.home.topOffers.heading')}
                            defaultSubheading={t('pages.home.topOffers.subheading')}
                            breadcrumbModel={breadcrumbModel}
                            offerModel={offerModel}
                            suitablesModel={suitablesModel}
                        />
                    )}
                    {!!recommendedObjects && recommendedObjects?.objects?.length > 0 && (
                        <TopRecommendationsSection
                            listingSection={recommendedObjects}
                            url={url}
                            defaultHeading={t('pages.home.topRecommendations.heading')}
                            breadcrumbModel={breadcrumbModel}
                            offerModel={offerModel}
                            suitablesModel={suitablesModel}
                        />
                    )}
                    {!!regionMap && <RegionCountSection regionsMap={regionMap} />}
                    {!!banners?.slot1 && <BannerSection banner={banners.slot1} />}
                    {!!topRatedObjectsSection && topRatedObjectsSection?.objects?.length > 0 && (
                        <ReviewSection
                            topRatedObjectsSection={topRatedObjectsSection}
                            breadcrumbList={breadcrumbModel}
                            url={url}
                            defaultHeading={t('pages.home.topRated.heading')}
                        />
                    )}
                    {!!otherPopularRegionsSection && otherPopularRegionsSection?.otherRegions?.length > 0 && (
                        <OtherPopularRegionSection regions={otherPopularRegionsSection.otherRegions} />
                    )}
                    {!!seoSection && <SeoSection seoQuestions={seoSection} />}
                    {!!faqSection && <FaqSection faqs={faqSection} />}
                    {!!banners?.slot2 && <BannerSection banner={banners.slot2} />}
                    {!!banners?.slot3 && <BannerSection banner={banners.slot3} />}
                    {!!newsLetterSection && <NewsLetterSection newsletter={newsLetterSection} url={url} />}
                </InnerContainer>
                {!!footerSection && (
                    <FooterSection
                        footerSection={footerSection}
                        footerLogo={headerSection.navigationSectionResponse.navigation.logo}
                        socialMediaLinks={headerSection.navigationSectionResponse.socialMediaLinks}
                    />
                )}
            </Container>
        </Fragment>
    )
}

export default HomePage
